import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import html2pdf from 'html2pdf.js'
import moment from 'moment'
import {Button} from 'primereact/button'
import {OverlayPanel} from 'primereact/overlaypanel'
import {Sidebar} from 'primereact/sidebar'
import {TabPanel, TabView} from 'primereact/tabview'
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import ReactDOM from 'react-dom'
import {useHistory, useParams} from 'react-router-dom/cjs/react-router-dom.min'
import SockJsClient from 'react-stomp'
import {API_BASE_URL, SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../../utils/BlockScrollsUtils'
import {useQueryParams} from '../../../utils/NavigatorUtils'
import {useAuth} from '../../authentication/AuthProvider'
import Information from '../../detail/Information'
import Statistic from '../../detail/Statistic'
import Discharge from '../../discharge/Discharge'
import PageContentWrapper from '../../layout/PageContentWrapper'
import NoteForm from '../../note/NoteForm'
import Observations from '../../observations/Observation'
import {
    setStoragePatientData,
    useDetailPatient,
} from '../../patient/query-keys/PatientKeysManager'
import CalendarForm from '../../reminder/CalendarForm'
import {Pharmacy} from '../../reminder/Reminder'
import PdfContent from '../../reporting/PdfContent'
import {permissionOptionKeys} from '../../role/RoleConstant'
import {useRoleContext} from '../../role/RoleProvider'
import {convertKeyToLabel} from '../../table-utils/TableUtils'
import {UserPropertyType} from '../../user-property/UserPropertyConstants'
import WatchConfig from '../../watch-config/WatchConfig'
import StyledSpeedDial from '../components/StyledSpeedDial'
import StyledExaminationDatatable from '../examination/ExaminationDataTable'
import {
    onUserSupportTeamDetailChangeListener,
    supportTeamEventTypes,
} from '../support-team/broadcast/SupportTeamEvent'
import {RefReloadEventTypes, onRefReloadListener} from './RefReloadEvent'
import StyledBasicInformation from './StyledBasicInformation'
import StyledDetailInformation from './StyledDetailInformation'
import {speedDialItemsKey} from './StyledDetailInformationConstants'
import './style/style.css'
const tabViewTypes = {
    information: 'information',
    observation: 'observation',
    parameter: 'parameter',
    statistic: 'statistic',
    symptom: 'symptom',
    reminder: 'reminder',
    notes: 'notes',
    examinations: 'examinations',
    nok: 'nok',
    discharge: 'discharge',
    watchConfig: 'watchConfig',
}

const WEBSOCKET_ENDPOINT = `${API_BASE_URL}`
const DESTINATION_PREFIX = '/ws/app'

const PatientContext = createContext({})

const StyleDetailInformationWrapper = () => {
    useSignals()

    //#reigon Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {patientManagementPermissions} = useRoleContext()

    //#endregion

    //#region Hooks
    const query = useQueryParams()
    const queryClient = useQueryClient()
    const history = useHistory()
    //#endregion

    //#region Translate
    const {translateKeys, translateCommons} = TranslateUtils()
    //#endregion

    const {id: idString} = useParams()
    const id = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])

    const {data, isFetched} = useDetailPatient(user?.id, id, !!id && !!user?.id)

    useEffect(() => {
        if (!!data?.id) {
            setStoragePatientData(id, data)
        }
    }, [data])

    //#region Form Ref Dialog
    const noteFormRef = useRef(null)
    const calendarFormRef = useRef(null)
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            if (
                userRole === SystemRole.ADMIN ||
                patientManagementPermissions?.includes(key)
            ) {
                return true
            }

            return false
        },
        [patientManagementPermissions, userRole],
    )

    //#region Breadcrumb
    const items = useMemo(() => {
        return [
            {
                label: 'Patient Management',
                command: () => history.push('/patients'),
            },
            {
                label: data?.fullName,
                template: () => (
                    <span className="font-bold text-darkcyan">
                        {data?.fullName}
                    </span>
                ),
            },
        ]
    }, [data])

    const home = {icon: 'pi pi-home', command: () => history.push('/')}
    //#endregion

    //#region TabView List
    const detailTabViewList = [
        {
            key: tabViewTypes.information,
            label: 'Information',
            disabled: !handleCheckPermissionByKey(
                permissionOptionKeys.allowToViewPatientInfo,
            ),
            component: () => <Information />,
            // component: () => <StyledDetailInformation hideProfile={true} />,
            profileFullScreen: false,
        },
        {
            key: tabViewTypes.observation,
            label: 'Observation',
            disabled: !handleCheckPermissionByKey(
                permissionOptionKeys.allowToViewObservations,
            ),
            component: () => <Observations />,
            hideProfile: true,
        },
        // {
        //     key: tabViewTypes.parameter,
        //     label: 'Parameter',
        //     disabled: !handleCheckPermissionByKey(
        //         permissionOptionKeys.allowToViewParameters,
        //     ),
        //     component: () => <ParameterForm />,
        //     profileFullScreen: true,
        // },
        {
            key: tabViewTypes.statistic,
            label: 'Activities',
            disabled: !handleCheckPermissionByKey(
                permissionOptionKeys.allowToViewActivities,
            ),
            component: () => (
                <Statistic
                    deviceAssigned={data?.deviceAssigned}
                    deviceBrand={data?.deviceBrand}
                    watchDeviceName={data?.watchDeviceName}
                    spatchDeviceName={data?.spatchDeviceName}
                />
            ),
            profileFullScreen: false,
        },
        // {
        //     key: tabViewTypes.symptom,
        //     label: 'Symptom',
        //     component: () => <PatientMedicalHistory userId={id} />,
        //     disabled: !handleCheckPermissionByKey(
        //         permissionOptionKeys.allowToViewSymptoms,
        //     ),
        //     profileFullScreen: true,
        // },
        {
            key: tabViewTypes.reminder,
            label: 'Reminder & Appointment',
            component: () => <Pharmacy />,
            disabled: !handleCheckPermissionByKey(
                permissionOptionKeys.allowToViewReminders,
            ),
            profileFullScreen: true,
        },
        // {
        //     key: tabViewTypes.notes,
        //     label: 'Notes',
        //     component: () => <StyledNoteDatatable />,
        //     disabled: !handleCheckPermissionByKey(
        //         permissionOptionKeys.allowToViewNotes,
        //     ),
        //     profileFullScreen: true,
        // },
        {
            key: tabViewTypes.examinations,
            label: 'Examination',
            component: () => <StyledExaminationDatatable />,
            disabled: !handleCheckPermissionByKey(
                permissionOptionKeys.allowToViewExaminations,
            ),
            profileFullScreen: true,
        },
        // {
        //     key: tabViewTypes.nok,
        //     label: 'Relevant Contact',
        //     component: () => <StyledRelevantContactDatatableDatatable />,
        //     disabled: !handleCheckPermissionByKey(
        //         permissionOptionKeys.allowToViewRelevantContacts,
        //     ),
        //     profileFullScreen: true,
        // },
        {
            key: tabViewTypes.discharge,
            label: 'Discharge Information',
            component: () => <Discharge userData={data} />,
            disabled: !handleCheckPermissionByKey(
                permissionOptionKeys.allowToViewDischargeInfo,
            ),
            profileFullScreen: true,
        },
        {
            key: tabViewTypes.watchConfig,
            label: 'Watch Configuration',
            component: () => <WatchConfig type="user" />,
            disabled: !handleCheckPermissionByKey(
                permissionOptionKeys.allowToViewWatchConfig,
            ),
            profileFullScreen: false,
        },
    ]

    const speedDialItems = {
        note: {
            label: 'Add Note',
            icon: <i className="pi pi-pencil mr-2"></i>,
            command: () => noteFormRef.current?.showForm(),
            isShow: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAddNotes,
            ),
        },
        reminder: {
            label: 'Add Reminder',
            icon: <i className="pi pi-clock mr-2"></i>,
            command: () => calendarFormRef.current?.showForm(),
            isShow: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAddReminders,
            ),
        },
    }

    const renderSpeedDialItemTemplate = type => {
        if (type) {
            const item = speedDialItems[type]
            return (
                item?.isShow && (
                    <div
                        className="flex align-items-center text-white surface-700 m-2 p-2 border-round-lg cursor-pointer hover:surface-500"
                        onClick={item?.command}>
                        {item?.icon}
                        <h5 className="m-0 font-normal">{item?.label}</h5>
                    </div>
                )
            )
        }

        return
    }

    const speedDialList = [
        {
            key: speedDialItemsKey.note,
            label: 'Add Note',
            icon: <i className="pi pi-pencil mr-2"></i>,
            command: () => noteFormRef.current?.showForm(),
            isShow: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAddNotes,
            ),
            // template: renderSpeedDialItemTemplate(speedDialItemsKey.note),
        },
        {
            key: speedDialItemsKey.reminder,
            label: 'Add Reminder',
            icon: <i className="pi pi-clock mr-2"></i>,
            command: () => calendarFormRef.current?.showForm(),
            isShow: handleCheckPermissionByKey(
                permissionOptionKeys.allowToAddReminders,
            ),
            // template: renderSpeedDialItemTemplate(speedDialItemsKey.reminder),
        },
    ]

    const handleOnClickSpeedDial = key => {
        const item = speedDialList.find(item => item.key === key)

        if (item) {
            item?.command()
        }
    }
    //#endregion

    //#region TabView Controls
    const tabViewRef = useRef(null)
    const getCurrentTabView = useMemo(() => {
        if (query && !!query?.get('tv')) {
            return query.get('tv')
        }

        localStorage.setItem(
            'detailActiveTabView',
            query.get('tv') || 'information',
        )

        return 'information'
    }, [query])

    const currentTabView = useSignal(getCurrentTabView)

    const currentTabIndex = useMemo(() => {
        return detailTabViewList.findIndex(
            item => item.key === currentTabView.value,
        )
    }, [currentTabView.value])

    const handleOnBeforeTabChange = e => {
        if (tabViewRef.current) {
            tabViewRef.current.activeIndex = e.index
        }

        const indexKey = detailTabViewList[e.index].key

        window.history.replaceState(
            null,
            'TaVIE',
            `/patients/${id}?tv=${!!indexKey ? indexKey : ''}`,
        )

        localStorage.setItem('detailActiveTabView', indexKey)

        window.removeEventListener('resize', () => {})
    }
    //#endregion

    //#region Websocket

    const websocketRef = useRef(null)

    const handleOnMessageCallback = payload => {
        console.log('---- Websocket Message ---- ', payload)
        if (!!payload) {
            if (
                (!payload?.userId &&
                    payload?.reloadDataType !== 'watchActivate') ||
                (payload?.userId === user?.id &&
                    payload?.reloadDataType !== 'supportTeam' &&
                    payload?.reloadDataType !== 'gp')
            ) {
                return
            }

            let payloadData

            try {
                payloadData = JSON.parse(payload?.data)
            } catch (error) {}

            switch (payload?.reloadDataType) {
                case 'watchActivate':
                case 'gp':
                case 'user':
                    if (!payloadData && !payloadData?.id) {
                        return
                    }

                    if (payloadData && typeof payloadData !== 'string') {
                        handleChangeCurrentViewedUser(payloadData)
                        const id = payloadData?.id
                        if (!!id) {
                            onRefReloadListener(
                                RefReloadEventTypes.DETAIL_INFORMATION,
                                id,
                            )
                        }
                    }

                    break
                case 'healthRecord':
                    const {healthRecordKeys} = QueryKeyManager(
                        QueryKeyTypes.healthRecord,
                        user?.id,
                    )

                    queryClient.invalidateQueries(healthRecordKeys.pattern())
                    break
                case 'parameter':
                    const {parameterKeys} = QueryKeyManager(
                        QueryKeyTypes.parameter,
                        user?.id,
                    )

                    queryClient.invalidateQueries(
                        parameterKeys.detailByUser({
                            userId: payloadData?.userId,
                        }),
                    )

                    break
                case 'healthActivity':
                    const {healthActivitiesKeys} = QueryKeyManager(
                        QueryKeyTypes.healthActivities,
                        user?.id,
                    )

                    queryClient.invalidateQueries(
                        healthActivitiesKeys.pattern(),
                    )
                    break
                case 'userSymptom':
                    const {userPropertyKeys} = QueryKeyManager(
                        QueryKeyTypes.userProperty,
                        user?.id,
                    )

                    queryClient.invalidateQueries(
                        userPropertyKeys.pattern({
                            type: UserPropertyType.userSymptom,
                        }),
                    )

                    break
                case 'reminder':
                    const {reminderKeys} = QueryKeyManager(
                        QueryKeyTypes.reminder,
                        user?.id,
                    )

                    queryClient.invalidateQueries(reminderKeys.pattern())
                    break
                case 'note':
                    const {noteKeys} = QueryKeyManager(
                        QueryKeyTypes.note,
                        user?.id,
                    )

                    queryClient.invalidateQueries(noteKeys.pattern())
                    break
                case 'nok':
                    const {nextOfKinKeys} = QueryKeyManager(
                        QueryKeyTypes.nextOfKin,
                        user?.id,
                    )

                    queryClient.invalidateQueries(nextOfKinKeys.pattern())
                    break
                case 'dischargeInformation':
                    const {dischargeKeys} = QueryKeyManager(
                        QueryKeyTypes.discharge,
                        user?.id,
                    )

                    queryClient.invalidateQueries(dischargeKeys.pattern())
                    break
                case 'examination':
                    const {examinationKeys} = QueryKeyManager(
                        QueryKeyTypes.examination,
                        user?.id,
                    )

                    queryClient.invalidateQueries(examinationKeys.pattern())
                    break
                case 'watchConfig':
                    const {watchConfigKeys} = QueryKeyManager(
                        QueryKeyTypes.watchConfig,
                        user?.id,
                    )

                    queryClient.invalidateQueries(watchConfigKeys.pattern())
                    break
                case 'ecg':
                    const {ecgKeys} = QueryKeyManager(
                        QueryKeyTypes.ecg,
                        user?.id,
                    )

                    queryClient.invalidateQueries(
                        ecgKeys.detailByUser({
                            userId: payloadData?.userId,
                            brand: payloadData?.brand,
                        }),
                    )
                    break
                case 'userSupportTeam':
                    const {supportTeamKeys} = QueryKeyManager(
                        QueryKeyTypes.supportTeam,
                        user?.id,
                    )

                    queryClient.invalidateQueries(supportTeamKeys.pattern())
                    onUserSupportTeamDetailChangeListener(
                        supportTeamEventTypes.USER_SUPPORT_TEAM_CHANGE,
                        payloadData,
                    )
                    break
                case 'biometric':
                    const {biometricsKeys} = QueryKeyManager(
                        QueryKeyTypes.biometrics,
                        user?.id,
                    )

                    queryClient.invalidateQueries(biometricsKeys.pattern())
                    break
                case 'wifi_connection':
                    const {networkSuggestionsKeys} = QueryKeyManager(
                        QueryKeyTypes.networkSuggestions,
                        user?.id,
                    )

                    queryClient.invalidateQueries(
                        networkSuggestionsKeys.pattern(),
                    )
                    break
                default:
                    break
            }
        }
    }

    const handleChangeCurrentViewedUser = newUserData => {
        if (!newUserData || !newUserData?.id) {
            return
        }

        const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, user?.id)

        queryClient.setQueryData(
            patientKeys.detail({id: newUserData?.id}),
            () => newUserData,
        )
    }
    //#endregion

    //#region Export Information
    const renderPdfPatients = data => {
        const htmlElements = []
        if (!!data && data?.length > 0) {
            for (let i = 0; i < data?.length; i++) {
                htmlElements.push(
                    <div
                        key={i}
                        style={{
                            pageBreakBefore: `${i === 0 ? 'avoid' : 'always'}`, // Break page after page 1
                        }}
                        className="content border-bottom-2 border-700 shadow relative mb-3 py-2">
                        <PdfContent
                            index={data[i]?.index + 1}
                            exportData={data[i]}
                            basicPatient={true}
                        />
                    </div>,
                )
            }
        }

        return htmlElements
    }

    const handleOnExportPdf = async () => {
        const element = document.getElementById('pdf-content')
        const cloneElement = element.cloneNode(true)
        cloneElement.setAttribute('class', 'block')

        const opt = {
            filename: `Patient_Information_${moment().toString()}.pdf`,
            image: {type: 'jpeg', quality: 0.98},
            html2canvas: {useCORS: true},
            jsPDF: {
                unit: 'in',
                format: 'a4',
                orientation: 'portrait',
            },
        }

        await html2pdf().set(opt).from(cloneElement).save()
    }

    const onExportInformation = async () => {
        let element = document.getElementById('pdf-content')
        let content = await renderPdfPatients([data])

        if (element && content) {
            // Render the JSX returned by renderPdfPatients into the target div
            await ReactDOM.render(content, element)
        }

        handleOnExportPdf()
        // Optionally cleanup by unmounting the component
        return () => {
            if (element) {
                ReactDOM.unmountComponentAtNode(element)
            }
        }
    }
    //#endregion
    const visible = useSignal(false)
    const op = useRef(null)
    return (
        <React.Fragment>
            <PatientContext.Provider
                value={{
                    currentViewedUser: data,
                    handleCheckPermissionByKey: handleCheckPermissionByKey,
                    handleChangeCurrentViewedUser:
                        handleChangeCurrentViewedUser,
                }}>
                <div className="grid ">
                    <div className="basic-info-wrapper relative">
                        <div
                            className=" col-12 m-0 bg-white sticky-basic-info "
                            style={{height: '94.5vh'}}>
                            <StyledBasicInformation />
                        </div>
                    </div>
                    <div className="patient-info-wrapper p-2">
                        <PageContentWrapper>
                            <div className="flex justify-content-between align-items-center w-full md:px-2 lg:px-2 mb-3">
                                <div className=" flex gap-2">
                                    <div className="xl:hidden">
                                        <Button
                                            className="bg-blue-50 text-blue-500 border-blue-50"
                                            icon="pi pi-user"
                                            label="View Information"
                                            onClick={() => {
                                                blockOverflownScroll()
                                                visible.value = true
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="hidden lg:block  text-700 font-semibold ">
                                    <div className="flex">
                                        <Button
                                            icon="pi pi-calendar "
                                            label={translateCommons(
                                                'Schedule a appointment',
                                            )}
                                            onClick={() =>
                                                calendarFormRef.current?.showForm()
                                            }
                                        />
                                        <Button
                                            className="border-round bg-white p-button-plain p-button-text ml-2 hover:bg-primary-reverse"
                                            icon="pi pi-print"
                                            label={translateCommons(
                                                'Print Profile',
                                            )}
                                            onClick={onExportInformation}
                                        />
                                    </div>
                                </div>
                                <div className="lg:hidden">
                                    <Button
                                        label="Print & Appointment"
                                        onClick={e => op.current.show(e)}
                                    />{' '}
                                </div>
                            </div>
                            <div className="grid">
                                <div className="col-12 ">
                                    {' '}
                                    <TabView
                                        ref={tabViewRef}
                                        activeIndex={currentTabIndex}
                                        onBeforeTabChange={e =>
                                            handleOnBeforeTabChange(e)
                                        }
                                        scrollable
                                        className="text-lg p-0">
                                        {detailTabViewList?.map(
                                            (tabPanelItem, index) => (
                                                <TabPanel
                                                    key={tabPanelItem.key}
                                                    header={
                                                        tabPanelItem?.label
                                                            ? translateKeys(
                                                                  tabPanelItem.label,
                                                              )
                                                            : translateKeys(
                                                                  convertKeyToLabel(
                                                                      tabPanelItem.key,
                                                                  ),
                                                              )
                                                    }
                                                    leftIcon={tabPanelItem.icon}
                                                    rightIcon={
                                                        tabPanelItem.icon
                                                    }
                                                    disabled={
                                                        tabPanelItem.disabled
                                                    }>
                                                    {!tabPanelItem.hideProfile && (
                                                        <StyledDetailInformation
                                                            profileFullScreen={
                                                                tabPanelItem.profileFullScreen
                                                            }
                                                        />
                                                    )}

                                                    {tabPanelItem.component()}
                                                </TabPanel>
                                            ),
                                        )}
                                    </TabView>
                                </div>
                            </div>
                            <div className=" ">
                                <Sidebar
                                    className="xl:hidden relative"
                                    visible={visible.value}
                                    onHide={() => {
                                        unblockOverflownScroll()
                                        visible.value = false
                                    }}
                                    modal={false}
                                    dismissable={false}
                                    style={{width: '27rem'}}>
                                    <div className="pt-6 ">
                                        <StyledBasicInformation />
                                    </div>
                                    <div className="p-2 absolute top-0 right-0">
                                        <Button
                                            className="p-button-text border-circle"
                                            icon={'pi pi-times'}
                                            onClick={() => {
                                                unblockOverflownScroll()
                                                visible.value = false
                                            }}
                                        />
                                    </div>
                                </Sidebar>
                            </div>
                        </PageContentWrapper>{' '}
                    </div>
                </div>

                <StyledSpeedDial
                    model={speedDialList}
                    callBack={handleOnClickSpeedDial}
                />

                {/* <SpeedDial
                    id="speed-dial"
                    rotateAnimation
                    model={speedDialList}
                    showIcon="pi pi-bars"
                    hideIcon="pi pi-times"
                    className="m-4 md:text-sm lg:text-md text-xs"
                    radius={70}
                    // direction="up"
                    direction="up-left"
                    type="quarter-circle"
                    style={{position: 'fixed', right: 0, bottom: 0}}
                /> */}

                {/* Form Ref Dialog */}
                <NoteForm ref={noteFormRef} />
                <CalendarForm ref={calendarFormRef} />
                <OverlayPanel ref={op}>
                    <div>
                        <Button
                            className="col-12 py-3 bg-gray-50 border-gray-100 text-700 mb-1"
                            icon="pi pi-calendar "
                            label={translateCommons('Schedule a appointment')}
                            onClick={() => calendarFormRef.current?.showForm()}
                        />
                        <Button
                            className="col-12 py-3 bg-gray-50 border-gray-100 text-700"
                            icon="pi pi-print"
                            label={translateCommons('Print Profile')}
                            onClick={onExportInformation}
                        />
                    </div>
                </OverlayPanel>
            </PatientContext.Provider>

            {!!data?.id && !!data?.username && (
                <SockJsClient
                    heartbeat={3000}
                    url={`${WEBSOCKET_ENDPOINT}/websocket`}
                    headers={{
                        username: `${data?.username}`,
                    }}
                    autoReconnect={true}
                    topics={[`/topic/user/${data?.id}`]}
                    onConnect={() => {
                        console.log('---- Websocket Connected For Detail ---- ')
                    }}
                    onDisconnect={() => {
                        console.log(
                            '---- Websocket Disconnected For Detail ---- ',
                        )
                    }}
                    onMessage={msg => {
                        handleOnMessageCallback(msg)
                    }}
                    ref={websocketRef}
                />
            )}
        </React.Fragment>
    )
}

export const usePatientContext = () => useContext(PatientContext)

export default StyleDetailInformationWrapper
